import { Check, Hash, MapPin, Search, Users } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import type {
  InstagramDiscoveryCandidate,
  InstagramLocalityEvidence,
} from '../../lib/workflowsApi';
import { colors } from '../../theme/colors';
import { Badge } from '../primitives/Badge';
import { Box } from '../primitives/Box';
import { Input } from '../primitives/Input';
import { HStack, VStack } from '../primitives/Stack';
import { Text } from '../primitives/Text';
import { clampMaxResults, type useInstagramDiscovery } from './useInstagramDiscovery';

type Discovery = ReturnType<typeof useInstagramDiscovery>;

interface InstagramDiscoveryPanelProps {
  readonly discovery: Discovery;
  readonly onDone: () => void;
}

/**
 * FLOSEAI-635 — the discovery half of the Instagram workflow.
 *
 * Two steps by design. Search spends Apify credit but creates nothing, so the
 * cost is stated before the run and the user picks which accounts are worth
 * enriching. Importing hands the selection to the ordinary enrichment queue.
 */
export function InstagramDiscoveryPanel({ discovery, onDone }: InstagramDiscoveryPanelProps) {
  const { t } = useTranslation();
  const [segmentName, setSegmentName] = useState('');
  const [maxResultsText, setMaxResultsText] = useState(String(discovery.criteria.maxResults));

  const committedMaxResults = discovery.criteria.maxResults;
  useEffect(() => {
    setMaxResultsText(String(committedMaxResults));
  }, [committedMaxResults]);

  const commitMaxResults = (): number => {
    const parsed = Number.parseInt(maxResultsText, 10);
    const next = Number.isNaN(parsed) ? committedMaxResults : clampMaxResults(parsed);
    discovery.updateCriteria({ maxResults: next });
    setMaxResultsText(String(next));
    return next;
  };

  const { phase, result, candidates, selectedCount, error } = discovery;
  const isSearching = phase === 'searching';
  const isImporting = phase === 'importing';
  const canSearch =
    discovery.hasCriteria && !isSearching && !isImporting && discovery.estimateReady;
  const submitSearch = () => {
    if (canSearch) discovery.search(commitMaxResults());
  };

  if (phase === 'imported') {
    return (
      <VStack spacing="md">
        <Box backgroundColor={colors.status.success.bg} borderRadius="md" padding="md">
          <HStack spacing="sm" align="center">
            <Check size={18} color={colors.status.success.main} strokeWidth={2} />
            <Text variant="bodySmall" color={colors.text.primary} style={styles.semibold}>
              {t('workflow.discovery.queued', { count: discovery.importedCount })}
            </Text>
          </HStack>
          <Text
            variant="caption"
            color={colors.text.secondary}
            style={{ marginTop: 6, lineHeight: 18 }}
          >
            {t('workflow.discovery.queuedHint')}
          </Text>
        </Box>
        <PrimaryButton label={t('workflow.discovery.done')} onPress={onDone} />
      </VStack>
    );
  }

  return (
    <VStack spacing="md">
      <Text variant="caption" color={colors.text.secondary} style={{ lineHeight: 18 }}>
        {t('workflow.discovery.intro')}
      </Text>

      <Input
        label={t('workflow.discovery.hashtagsLabel')}
        value={discovery.criteria.hashtags}
        onChangeText={(hashtags) => discovery.updateCriteria({ hashtags })}
        onSubmitEditing={submitSearch}
        returnKeyType="search"
        placeholder={t('workflow.discovery.hashtagsPlaceholder')}
        autoCapitalize="none"
        editable={!isSearching && !isImporting}
      />

      <Input
        label={t('workflow.discovery.keywordLabel')}
        value={discovery.criteria.keyword}
        onChangeText={(keyword) => discovery.updateCriteria({ keyword })}
        onSubmitEditing={submitSearch}
        returnKeyType="search"
        placeholder={t('workflow.discovery.keywordPlaceholder')}
        autoCapitalize="none"
        editable={!isSearching && !isImporting}
      />

      <Input
        label={t('workflow.discovery.placeLabel')}
        helperText={t('workflow.discovery.placeHelper')}
        value={discovery.criteria.place}
        onChangeText={(place) => discovery.updateCriteria({ place })}
        onSubmitEditing={submitSearch}
        returnKeyType="search"
        placeholder={t('workflow.discovery.placePlaceholder')}
        editable={!isSearching && !isImporting}
      />

      <Input
        label={t('workflow.discovery.maxResultsLabel')}
        value={maxResultsText}
        onChangeText={(raw) => setMaxResultsText(raw.replace(/\D/g, ''))}
        onBlur={commitMaxResults}
        keyboardType="number-pad"
        editable={!isSearching && !isImporting}
      />

      {phase !== 'results' && discovery.estimate?.limitedByPlan && (
        <Text variant="caption" color={colors.status.warning.main}>
          {t('workflow.discovery.limitedByPlan', { max: discovery.estimate.maxResults })}
        </Text>
      )}

      {/* A narrowed run without an explanation reads as a broken search. */}
      {result?.estimate?.limitedByPlan && (
        <Text variant="caption" color={colors.status.warning.main}>
          {t('workflow.discovery.limitedByPlan', { max: result.estimate.maxResults })}
        </Text>
      )}

      {error && (
        <Box backgroundColor={colors.status.error.bg} borderRadius="md" padding="md">
          <Text variant="caption" color={colors.status.error.main}>
            {error}
          </Text>
        </Box>
      )}

      <PrimaryButton
        label={isSearching ? t('workflow.discovery.searching') : t('workflow.discovery.search')}
        onPress={submitSearch}
        disabled={!canSearch}
        busy={isSearching}
        icon={<Search size={16} color={colors.text.inverse} strokeWidth={2} />}
      />

      {phase === 'results' && (
        <VStack spacing="sm">
          {result?.placeSearchFailed && candidates.length > 0 && (
            <Text variant="caption" color={colors.status.warning.main}>
              {t('workflow.discovery.placeSearchFailedWarning')}
            </Text>
          )}

          {(result?.failedSearches?.length ?? 0) > 0 && candidates.length > 0 && (
            <Text variant="caption" color={colors.status.warning.main}>
              {t('workflow.discovery.searchPartiallyFailedWarning')}
            </Text>
          )}

          {candidates.length === 0 ? (
            <EmptyState
              reason={result?.emptyReason}
              alreadyKnown={result?.alreadyKnown ?? 0}
              droppedForLocality={result?.droppedForLocality ?? 0}
              place={discovery.resultPlace}
              onRetry={discovery.retry}
            />
          ) : (
            <>
              <HStack spacing="sm" align="center" justify="between">
                <Text variant="bodySmall" color={colors.text.primary} style={styles.semibold}>
                  {t('workflow.discovery.found', {
                    count: candidates.length,
                    selected: selectedCount,
                  })}
                </Text>
                <TouchableOpacity
                  onPress={
                    selectedCount === candidates.length
                      ? discovery.clearSelection
                      : discovery.selectAll
                  }
                  accessibilityRole="button"
                >
                  <Text variant="caption" color={colors.text.interactive}>
                    {selectedCount === candidates.length
                      ? t('workflow.discovery.clearAll')
                      : t('workflow.discovery.selectAll')}
                  </Text>
                </TouchableOpacity>
              </HStack>

              {(result?.alreadyKnown ?? 0) > 0 && (
                <Text variant="caption" color={colors.text.tertiary}>
                  {t('workflow.discovery.alreadyKnown', { count: result?.alreadyKnown ?? 0 })}
                </Text>
              )}

              {(result?.droppedForLocality ?? 0) > 0 && (
                <Text variant="caption" color={colors.text.tertiary}>
                  {t('workflow.discovery.droppedForLocalityNote', {
                    count: result?.droppedForLocality ?? 0,
                    place: discovery.resultPlace,
                  })}
                </Text>
              )}

              {(result?.hiddenOneTimePosters ?? 0) > 0 && (
                <Text variant="caption" color={colors.text.tertiary}>
                  {t('workflow.discovery.hiddenOneTimePostersNote', {
                    count: result?.hiddenOneTimePosters ?? 0,
                  })}
                </Text>
              )}

              {candidates.map((candidate) => (
                <CandidateRow
                  key={candidate.username}
                  candidate={candidate}
                  selected={discovery.selected.has(candidate.username)}
                  onToggle={() => discovery.toggleCandidate(candidate.username)}
                  disabled={isImporting}
                />
              ))}

              <Input
                label={t('workflow.discovery.segmentNameLabel')}
                value={segmentName}
                onChangeText={setSegmentName}
                placeholder={t('workflow.discovery.segmentNamePlaceholder')}
                editable={!isImporting}
              />

              <PrimaryButton
                label={
                  isImporting
                    ? t('workflow.discovery.importing')
                    : t('workflow.discovery.import', { count: selectedCount })
                }
                onPress={() => discovery.importSelected(segmentName)}
                disabled={selectedCount === 0 || isImporting}
                busy={isImporting}
              />
            </>
          )}
        </VStack>
      )}
    </VStack>
  );
}

/**
 * An empty result always says why — "found nothing" must never read as a bug.
 *
 * `place_not_found` is distinct from a plain `no_results`: the place name
 * itself never resolved to an Instagram location, so the fix is a different
 * place name, not a broader hashtag or keyword (FLOSEAI-635 QA).
 *
 * `place_search_failed` is distinct again: the place actor run itself
 * rejected, so nothing about the place name is wrong. `no_local_match` names
 * how many candidates existed BEFORE narrowing (`droppedForLocality`), so the
 * user knows the search worked and place was the reason nothing survived.
 *
 * `search_failed` is distinct from all of the above: a hashtag/keyword run
 * (or, place-only, the place run itself) never completed at all, so the fix
 * is to retry, not to broaden or change any criterion — the search button is
 * hidden once results show, so this is the only way back to a usable form
 * without losing what the user typed (FLOSEAI-635 T13).
 */
function EmptyState({
  reason,
  alreadyKnown,
  droppedForLocality,
  place,
  onRetry,
}: Readonly<{
  reason?: string;
  alreadyKnown: number;
  droppedForLocality: number;
  place: string;
  onRetry: () => void;
}>) {
  const { t } = useTranslation();
  const key =
    reason === 'all_already_known'
      ? 'emptyAllKnown'
      : reason === 'place_not_found'
        ? 'emptyPlaceNotFound'
        : reason === 'place_search_failed'
          ? 'emptyPlaceSearchFailed'
          : reason === 'search_failed'
            ? 'emptySearchFailed'
            : reason === 'no_local_match'
              ? 'emptyNoLocalMatch'
              : 'emptyNoResults';
  const count = reason === 'no_local_match' ? droppedForLocality : alreadyKnown;

  return (
    <Box backgroundColor={colors.background.secondary} borderRadius="md" padding="md">
      <Text variant="bodySmall" color={colors.text.secondary} style={{ lineHeight: 20 }}>
        {t(`workflow.discovery.${key}`, { count, place })}
      </Text>
      {(reason === 'search_failed' ||
        reason === 'place_search_failed' ||
        reason === 'place_not_found') && (
        <TouchableOpacity
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel={t('workflow.discovery.tryAgain')}
          style={{ marginTop: 12 }}
        >
          <Text variant="bodySmall" color={colors.text.interactive} style={styles.semibold}>
            {t('workflow.discovery.tryAgain')}
          </Text>
        </TouchableOpacity>
      )}
    </Box>
  );
}

const LOCALITY_EVIDENCE_PRIORITY: InstagramLocalityEvidence[] = [
  'posted_at_place',
  'post_tagged_at_place',
  'profile_mentions_place',
];

/** Names the strongest kind of evidence a candidate earned, mirroring the backend's own ranking (FLOSEAI-635). */
function strongestLocalityReasonKey(evidence: InstagramLocalityEvidence[]): string {
  const strongest = LOCALITY_EVIDENCE_PRIORITY.find((kind) => evidence.includes(kind));
  switch (strongest) {
    case 'posted_at_place':
      return 'workflow.discovery.localityReasonPostedAtPlace';
    case 'post_tagged_at_place':
      return 'workflow.discovery.localityReasonPostTaggedAtPlace';
    default:
      return 'workflow.discovery.localityReasonProfileMentionsPlace';
  }
}

function CandidateRow({
  candidate,
  selected,
  onToggle,
  disabled,
}: Readonly<{
  candidate: InstagramDiscoveryCandidate;
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
}>) {
  const { t } = useTranslation();

  return (
    <Pressable
      onPress={onToggle}
      disabled={disabled}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected, disabled }}
      accessibilityLabel={`@${candidate.username}`}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        gap="sm"
        padding="md"
        borderRadius="md"
        borderWidth={1}
        borderColor={selected ? colors.primary[500] : colors.border.subtle}
        backgroundColor={selected ? colors.primary.muted : colors.background.primary}
      >
        <Box
          alignItems="center"
          justifyContent="center"
          style={[styles.checkbox, selected && styles.checkboxOn]}
        >
          {selected && <Check size={12} color={colors.text.inverse} strokeWidth={3} />}
        </Box>

        <VStack spacing="xxs" style={{ flex: 1 }}>
          <HStack spacing="xs" align="center">
            <Text variant="bodySmall" color={colors.text.primary} style={styles.semibold}>
              @{candidate.username}
            </Text>
            {candidate.isBusinessAccount && (
              <Badge variant="subtle" color="blue" size="sm">
                {t('workflow.discovery.business')}
              </Badge>
            )}
            {candidate.localityEvidence && candidate.localityEvidence.length > 0 && (
              <Badge
                variant="subtle"
                color="green"
                size="sm"
                accessibilityLabel={t(strongestLocalityReasonKey(candidate.localityEvidence))}
              >
                {t('workflow.discovery.localityBadge')}
              </Badge>
            )}
          </HStack>

          {candidate.fullName && (
            <Text variant="caption" color={colors.text.secondary} numberOfLines={1}>
              {candidate.fullName}
            </Text>
          )}

          <HStack spacing="md" align="center">
            {candidate.followersCount !== undefined && (
              <HStack spacing="xxs" align="center">
                <Users size={11} color={colors.text.tertiary} strokeWidth={1.8} />
                <Text variant="caption" color={colors.text.tertiary}>
                  {candidate.followersCount.toLocaleString()}
                </Text>
              </HStack>
            )}
            <HStack spacing="xxs" align="center">
              {candidate.discoveredVia.startsWith('#') ? (
                <Hash size={11} color={colors.text.tertiary} strokeWidth={1.8} />
              ) : (
                <MapPin size={11} color={colors.text.tertiary} strokeWidth={1.8} />
              )}
              <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
                {candidate.discoveredVia}
              </Text>
            </HStack>
            {candidate.matchCount > 1 && (
              <Text variant="caption" color={colors.primary.neon}>
                {t('workflow.discovery.matches', { count: candidate.matchCount })}
              </Text>
            )}
          </HStack>
        </VStack>
      </Box>
    </Pressable>
  );
}

function PrimaryButton({
  label,
  onPress,
  disabled,
  busy,
  icon,
}: Readonly<{
  label: string;
  onPress: () => void;
  disabled?: boolean;
  busy?: boolean;
  icon?: React.ReactNode;
}>) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: Boolean(disabled), busy: Boolean(busy) }}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        gap="sm"
        padding="md"
        borderRadius="md"
        backgroundColor={disabled ? colors.background.secondary : colors.primary[500]}
      >
        {busy ? <ActivityIndicator size="small" color={colors.text.inverse} /> : icon}
        <Text
          variant="body"
          color={disabled ? colors.text.tertiary : colors.text.inverse}
          style={styles.semibold}
        >
          {label}
        </Text>
      </Box>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  semibold: { fontWeight: '600' },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 1.5,
    borderColor: colors.border.subtle,
  },
  checkboxOn: {
    backgroundColor: colors.primary[500],
    borderColor: colors.primary[500],
  },
});
